import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router";
import { CSSTransition, TransitionGroup } from "react-transition-group";
import firebase from "services/firebase/firebase.config";
import { useAuthStore } from "stores/authStore/authStore";
import { useToastStore } from "stores/toastStore/toastStore";
import { toApiPhoneNumber } from "utils/phoneNumber.logic";
import { useCompanyStore } from "web-apps/company/stores/companyStore/companyStore";
import { CompanyInfoForm } from "../companyInfoForm/companyInfoForm.component";
import { Confirmation } from "../confirmation/confirmation.component";
import { ContactForm } from "../contactForm/contactForm.component";
import { createCompanyRegistrationRequest } from "./registrationContainer.logic";
import { Slide, PageSliderStyled } from "./registrationContainer.styled";

export const RegistrationContainer = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const [authState, authActions] = useAuthStore();
  const firebaseUser = firebase.auth().currentUser ?? authState.firestoreUser;
  const [companyState, companyActions] = useCompanyStore();
  const [page, setPage] = useState(0);
  const [companyName, setCompanyName] = useState("");
  const [registrationNumber, setRegistrationNumber] = useState("");
  const [legalName, setLegalName] = useState("");
  const [contactPersonEmail, setContactPersonEmail] = useState("");
  const [contactPersonPhoneNumber, setContactPersonPhoneNumber] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [city, setCity] = useState("");
  const [companyAdress, setCompanyAdress] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [location, setLocation] = useState("");
  const [prevPage, setPrevPage] = useState(0);
  const [, toastActions] = useToastStore();

  const showRegistrationFailed = (): void => {
    toastActions.showToast({
      variant: "error",
      message: t("RegisterCompany.SomethingWentWrong"),
    });
  };

  const register = async (): Promise<void> => {
    const hasRequiredCompanyData = Boolean(
      companyName &&
      registrationNumber &&
      legalName &&
      contactPersonPhoneNumber &&
      firstName &&
      lastName &&
      city &&
      companyAdress &&
      postalCode &&
      location,
    );

    if (!hasRequiredCompanyData) {
      showRegistrationFailed();
      return;
    }

    const apiPhoneNumber = toApiPhoneNumber(contactPersonPhoneNumber);
    if (!apiPhoneNumber.succeeded) {
      showRegistrationFailed();
      return;
    }

    const data = createCompanyRegistrationRequest({
      city,
      companyAdress,
      companyName,
      contactPersonApiPhoneNumber: apiPhoneNumber.data,
      contactPersonEmail,
      firebaseEmail:
        firebase.auth().currentUser?.email ??
        authState.firestoreUser?.email ??
        undefined,
      firstName,
      lastName,
      legalName,
      location,
      postalCode,
      registrationNumber,
    });

    try {
      await companyActions.saveCompany(data, companyState.companies);
      setPage((prevState) => prevState + 1);
    } catch {
      showRegistrationFailed();
    }
  };

  const handleLogin = async (): Promise<void> => {
    const signedInUser = firebase.auth().currentUser ?? authState.firestoreUser;

    if (!signedInUser) {
      showRegistrationFailed();
      return;
    }

    try {
      await authActions.loginEmailAndPassword(signedInUser);
      history.push("/company/schedule");
    } catch {
      showRegistrationFailed();
    }
  };

  const renderSlide = () => {
    switch (page) {
      case 0: {
        return (
          <CompanyInfoForm
            page={page}
            setPage={setPage}
            companyName={companyName}
            setCompanyName={setCompanyName}
            registrationNumber={registrationNumber}
            setRegistrationNumber={setRegistrationNumber}
            legalName={legalName}
            setLegalName={setLegalName}
            postalCode={postalCode}
            setPostalCode={setPostalCode}
            city={city}
            setCity={setCity}
            companyAdress={companyAdress}
            setCompanyAdress={setCompanyAdress}
            location={location}
            setLocation={setLocation}
          />
        );
      }
      case 1: {
        return (
          <ContactForm
            page={page}
            setPage={setPage}
            firstName={firstName}
            setFirstName={setFirstName}
            lastName={lastName}
            setLastName={setLastName}
            contactPersonEmail={contactPersonEmail}
            contactPersonPhoneNumber={contactPersonPhoneNumber}
            setContactPersonPhoneNumber={setContactPersonPhoneNumber}
            register={register}
          />
        );
      }
      case 2: {
        return <Confirmation handleLogin={handleLogin} />;
      }
    }
  };

  useEffect(() => {
    if (firebaseUser?.email) {
      setContactPersonEmail(firebaseUser.email);
    }
  }, [firebaseUser]);

  useEffect(() => {
    setPrevPage(page);

    const bodyEl = document.querySelector("body");
    if (bodyEl) bodyEl.scrollIntoView();
  }, [page]);

  return (
    <TransitionGroup
      component={PageSliderStyled}
      childFactory={(child) =>
        React.cloneElement(child, {
          classNames: `${
            prevPage < page ? "left" : prevPage > page ? "right" : "_"
          } slide`,
        })
      }
    >
      <CSSTransition
        key={page}
        timeout={{ enter: 600, exit: 600 }}
        mountOnEnter={false}
        unmountOnExit={true}
      >
        <Slide className="page">{renderSlide()}</Slide>
      </CSSTransition>
    </TransitionGroup>
  );
};
